import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FileText, Filter, Plus, X } from "lucide-react";

import { useCreateRfq, useRfqs } from "@/api/queries";
import type { Rfq, RfqStatus } from "@/api/types";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { Pagination, TableShell, Td, Th, Tr } from "@/components/DataTable";
import { Field, Input, Select } from "@/components/Input";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import { formatDate, formatRfqStatus, pluralize } from "@/lib/format";
import { rfqTone } from "@/lib/stock";

const PAGE_SIZE = 50;

const STATUSES: RfqStatus[] = ["DRAFT", "READY_TO_SEND", "CANCELLED"];

/**
 * Les demandes de prix (V1.6-04).
 *
 * Une demande de prix demande une information commerciale à un fournisseur.
 * Elle ne commande rien, ne réserve rien et ne touche pas au stock — et dans
 * cette version **elle ne part pas** : figer arrête le contenu, la transmission
 * reste à faire à la main.
 */
export function RfqsPage() {
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);

  // Ouvrir la création depuis un lien — le dashboard s'en sert. Le paramètre
  // est consommé tout de suite pour que revenir en arrière ne rouvre pas la
  // boîte, et pour qu'un rafraîchissement ne la rouvre pas non plus.
  useEffect(() => {
    if (params.get("new") === null) return;
    setCreating(true);
    const merged = new URLSearchParams(params);
    merged.delete("new");
    setParams(merged, { replace: true });
  }, [params, setParams]);


  const status = params.get("status") ?? undefined;
  const offset = Number(params.get("offset") ?? 0);

  const query = useMemo(
    () => ({
      status: status as RfqStatus | undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [status, offset],
  );

  const { data, isLoading, error, refetch } = useRfqs(query);

  function update(next: Record<string, string | undefined>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === undefined || value === "") merged.delete(key);
      else merged.set(key, value);
    }
    if (!("offset" in next)) merged.delete("offset");
    setParams(merged, { replace: true });
  }

  return (
    <Page>
      <PageHeader
        title="Demandes de prix"
        subtitle="Ce qu'on demande aux fournisseurs. Une demande n'est pas une commande."
        actions={
          <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
            <Plus className="size-4" aria-hidden />
            Nouvelle demande
          </Button>
        }
      />

      <div className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-surface">
        <div className="flex shrink-0 flex-col gap-2 border-b border-border p-3 sm:flex-row sm:items-center">
          <Select
            value={status ?? ""}
            onChange={(event) => update({ status: event.target.value || undefined })}
            aria-label="Filtrer par statut"
            className="sm:w-56"
          >
            <option value="">Tous les statuts</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {formatRfqStatus(value)}
              </option>
            ))}
          </Select>

          {status && (
            <button
              type="button"
              onClick={() => setParams(new URLSearchParams(), { replace: true })}
              className="flex h-10 items-center gap-1.5 rounded-md px-2 text-[13px] text-ink-subtle hover:text-ink"
            >
              <X className="size-4" aria-hidden />
              Effacer
            </button>
          )}
        </div>

        <div className="min-w-0">
          {isLoading && <TableSkeleton rows={8} columns={5} />}
          {error && <ErrorState error={error} onRetry={() => void refetch()} />}

          {data && data.items.length === 0 && (
            <EmptyState
              icon={
                status ? (
                  <Filter className="size-6" aria-hidden />
                ) : (
                  <FileText className="size-6" aria-hidden />
                )
              }
              title={status ? "Aucune demande ne correspond" : "Aucune demande de prix"}
              description={
                status
                  ? "Essayez un autre statut."
                  : "Une demande de prix pose des questions à un fournisseur. Elle n'engage aucun achat."
              }
            />
          )}

          {data && data.items.length > 0 && (
            <>
              <div className="hidden md:block">
                <TableShell>
                  <thead>
                    <tr>
                      <Th className="w-32 whitespace-nowrap">Code</Th>
                      <Th className="max-w-0">Demande</Th>
                      <Th className="hidden whitespace-nowrap @4xl:table-cell">Préparée</Th>
                      <Th align="right" className="whitespace-nowrap">
                        Lignes
                      </Th>
                      <Th align="right" className="whitespace-nowrap">
                        Fournisseurs
                      </Th>
                      <Th>Statut</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((item) => (
                      <RfqRow key={item.id} item={item} />
                    ))}
                  </tbody>
                </TableShell>
              </div>

              <ul className="divide-y divide-border/60 md:hidden">
                {data.items.map((item) => (
                  <li key={item.id}>
                    <RfqCard item={item} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {data && data.total > PAGE_SIZE && (
          <Pagination
            offset={offset}
            limit={PAGE_SIZE}
            total={data.total}
            onChange={(next) => update({ offset: String(next) })}
          />
        )}
      </div>

      <NewRfqDialog open={creating} onOpenChange={setCreating} />
    </Page>
  );
}

function RfqRow({ item }: { item: Rfq }) {
  return (
    <Tr>
      <Td className="whitespace-nowrap font-mono text-[12px] text-ink-muted">
        <Link to={`/rfqs/${item.id}`} className="hover:text-brand">
          {item.code}
        </Link>
      </Td>
      <Td className="max-w-0">
        <Link to={`/rfqs/${item.id}`} className="block min-w-0 hover:text-brand">
          <span className="block truncate font-medium text-ink">{item.title}</span>
          {item.description && (
            <span className="block truncate text-[11px] text-ink-subtle">{item.description}</span>
          )}
        </Link>
      </Td>
      <Td className="hidden whitespace-nowrap text-[12px] text-ink-muted @4xl:table-cell">
        {formatDate(item.created_at)}
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink-muted">
        {item.line_count}
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink-muted">
        {item.supplier_count}
      </Td>
      <Td>
        <StatusBadge tone={rfqTone(item.status)}>{formatRfqStatus(item.status)}</StatusBadge>
      </Td>
    </Tr>
  );
}

function RfqCard({ item }: { item: Rfq }) {
  return (
    <Link
      to={`/rfqs/${item.id}`}
      className="flex min-h-[64px] items-center gap-3 px-3 py-3 active:bg-surface-hover"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-medium text-ink">{item.title}</p>
        <p className="truncate font-mono text-[11px] text-ink-subtle">{item.code}</p>
        <p className="mt-0.5 truncate text-[12px] text-ink-muted">
          {pluralize(item.line_count, "ligne")} · {pluralize(item.supplier_count, "fournisseur")}
        </p>
      </div>
      <StatusBadge tone={rfqTone(item.status)} className="shrink-0">
        {formatRfqStatus(item.status)}
      </StatusBadge>
    </Link>
  );
}

function NewRfqDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const createRfq = useCreateRfq();
  const [title, setTitle] = useState("");
  const [requiredAt, setRequiredAt] = useState("");

  function reset() {
    setTitle("");
    setRequiredAt("");
  }

  async function submit() {
    if (!title.trim()) return;
    try {
      const created = await createRfq.mutateAsync({
        title,
        required_at: requiredAt || null,
      });
      toast.success("Demande créée", `${created.code} — brouillon à remplir`);
      reset();
      onOpenChange(false);
    } catch (error) {
      toast.error("Demande non créée", String(error));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title="Nouvelle demande de prix"
      description="Un brouillon, à remplir puis à figer. Rien n'est envoyé."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={!title.trim() || createRfq.isPending}
          >
            {createRfq.isPending ? "Création…" : "Créer"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Objet de la demande">
          <Input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Panneau et disjoncteurs"
            aria-label="Objet de la demande"
          />
        </Field>
        <Field label="Date souhaitée" hint="Facultatif. Ce qu'on indique au fournisseur.">
          <Input
            type="date"
            value={requiredAt}
            onChange={(event) => setRequiredAt(event.target.value)}
            aria-label="Date souhaitée"
          />
        </Field>
      </div>
    </Dialog>
  );
}
